/**
 * Enabled states for Keycloak authentication.
 *
 * - Enabled:  Keycloak is the only supported auth flow.
 * - Optional: Keycloak is available alongside the legacy auth flow.
 * - Disabled: Keycloak is not available; use the legacy auth flow.
 */
export const KeycloakMode = Object.freeze({
  Enabled: "enabled",
  Optional: "optional",
  Disabled: "disabled",
});

/**
 * Resolves the configured Keycloak mode from the AUTH_KEYCLOAK_MODE environment
 * variable. The value is matched case-insensitively against {@link KeycloakMode}.
 * Any missing or unrecognized value falls back to {@link KeycloakMode.Disabled}.
 *
 * @return {string} The resolved Keycloak mode (a {@link KeycloakMode} value).
 */
export const getKeycloakMode = () => {
  switch ((process.env.AUTH_KEYCLOAK_MODE || "").trim().toLowerCase()) {
    case KeycloakMode.Enabled:
      return KeycloakMode.Enabled;
    case KeycloakMode.Optional:
      return KeycloakMode.Optional;
    default:
      return KeycloakMode.Disabled;
  }
};

/**
 * Determines whether the Keycloak auth flow should be used.
 *
 * Priority:
 * 1. ?disable_new_auth=true query param → disables and clears localStorage flag
 * 2. localStorage DEV_USE_KEYCLOAK=true (non-production only)
 * 3. process.env.AUTH_USE_KEYCLOAK=true
 *
 * Uses ENVIRONMENT_NAME (deployment env) to detect production, falling back
 * to NODE_ENV when ENVIRONMENT_NAME is not defined or empty.
 * @return {boolean} Whether Keycloak auth is enabled.
 */
export const isKeycloakAuthEnabled = () => {
  const isProduction =
    process.env.ENVIRONMENT_NAME ?
      process.env.ENVIRONMENT_NAME === "production" :
      process.env.NODE_ENV === "production";

  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("disable_new_auth") === "true") {
      window.localStorage.removeItem("DEV_USE_KEYCLOAK");
      params.delete("disable_new_auth");
      const newUrl =
        params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      return false;
    }
  }

  if (
    !isProduction &&
    typeof window !== "undefined" &&
    window.localStorage.getItem("DEV_USE_KEYCLOAK") === "true"
  ) {
    return true;
  }

  return process.env.AUTH_USE_KEYCLOAK === "true";
};
